// Zod DTOs for the Catalogue / Discovery API (P3-17, spec §4/§6.3) —
// `GET /v1/me/catalogue`, `GET /v1/me/catalogue/summary`, `GET
// /v1/catalogue/public`. A PRESENTATION PROJECTION over the §4
// effective-access set (`../../apps/api/src/services/access.ts`'s FROZEN
// `resolveEffectiveAccess`) — this module has no opinion on access
// resolution itself, only on how an already-resolved `Resource` row is
// shaped for the Home/Library screens.
//
// ─── FROZEN CONTRACT (ticket #5) ───────────────────────────────────────────
// Once shipped, `CatalogueCard` / the `Page<CatalogueCard>` envelope /
// `CatalogueSummary` shapes are frozen and must not drift — see this
// ticket's own "FROZEN CONTRACT" note. Any reshape needs a
// software-architect sign-off, same bar as `EffectiveAccess` in
// `services/access.ts`.
import { z } from 'zod';
import { ResourceType } from '../enums';
import { idSchema, paginationQuerySchema, paginatedResponseSchema } from './primitives';

// ─── GET /v1/me/catalogue ───────────────────────────────────────────────────

/** `sort=popular|newest|free_first` (ticket AC). `popular` currently has NO
 * real popularity signal in the schema (no view/enrollment-click tracking
 * table exists yet) — `../../apps/api/src/services/catalogue.ts`'s
 * `sortCards` documents the honest fallback (same ordering as `newest`)
 * rather than fabricating a fake metric; flagged there for a follow-up
 * ticket once real usage analytics exist. */
export const catalogueSortSchema = z.enum(['popular', 'newest', 'free_first']);
export type CatalogueSort = z.infer<typeof catalogueSortSchema>;

/** Machine-matchable `Resource.type` vocabulary (§3.3 `ResourceType` CHECK:
 * `NOTE|VIDEO|SIMULATION|PDF|LINK`) — reused both as `CatalogueCard.resourceType`
 * and as the optional `?resourceType=` filter below. */
export const catalogueResourceTypeSchema = z.enum(
  Object.values(ResourceType) as [ResourceType, ...ResourceType[]],
);

export const meCatalogueQuerySchema = paginationQuerySchema.extend({
  /** Free-text query — when present the handler delegates to `GET
   * /v1/me/search` (FTS5, P3-07) and re-shapes its hits into cards; it never
   * runs its own search over `Resource` (ticket AC). `sort`/`topic` are
   * ignored when `q` is present (search owns relevance ranking for that
   * request — see the service's doc comment). */
  q: z.string().trim().min(1).max(200).optional(),
  /** Case-insensitive substring match against the owning `Subject.name`/
   * `slug` OR `Section.title`/`slug` ("filters by subject/topic", ticket
   * AC) — ignored when `q` is present. */
  topic: z.string().trim().min(1).max(200).optional(),
  sort: catalogueSortSchema.default('newest'),
  /** ADDITIVE PARAM (pre-freeze, folded into this ticket): filters the
   * candidate set down to a single `Resource.type` (e.g. `SIMULATION`) —
   * `P3-FE-03` ("My sims") is a filtered view over this same endpoint, not a
   * new route. Applied server-side to the WHERE clause BEFORE
   * `LIMIT`/cursor pagination — filtering after pagination would return
   * short pages with a misleading non-null `nextCursor`. No shape change to
   * `CatalogueCard`. */
  resourceType: catalogueResourceTypeSchema.optional(),
});
export type MeCatalogueQuery = z.infer<typeof meCatalogueQuerySchema>;

/** Fixed badge-KIND vocabulary (ticket: `badges[FREE|LEVEL|LIVE|NEW]`) — not
 * display text, a machine-matchable kind the client renders its own pill
 * copy for. `LIVE` is wired end-to-end (schema + query + DTO) but can never
 * currently be produced — this app's `Resource.type` has no
 * live/scheduled-session concept yet (§3.3's `ResourceType` CHECK is
 * `NOTE|VIDEO|SIMULATION|PDF|LINK`); reserved so a future live-session
 * feature doesn't need a `CatalogueCard` contract change. */
export const catalogueBadgeSchema = z.enum(['FREE', 'LEVEL', 'LIVE', 'NEW']);
export type CatalogueBadge = z.infer<typeof catalogueBadgeSchema>;

export const catalogueProvenanceSchema = z.object({
  level: z.string(),
  subject: z.string(),
  section: z.string(),
});
export type CatalogueProvenance = z.infer<typeof catalogueProvenanceSchema>;

/** FROZEN shape (ticket body, verbatim field list) — `{resourceId, title,
 * summary, resourceType, tag, provenance{level, subject, section},
 * badges[FREE|LEVEL|LIVE|NEW], thumbnailUrl, isFreePreview, locked}`. */
export const catalogueCardSchema = z.object({
  resourceId: idSchema,
  title: z.string(),
  summary: z.string().nullable(),
  resourceType: catalogueResourceTypeSchema,
  /** Topic/strand eyebrow — the owning `Subject.name` (ticket: `tag`, e.g.
   * "MECHANICS" per the Home mockup's card eyebrow). */
  tag: z.string(),
  provenance: catalogueProvenanceSchema,
  badges: z.array(catalogueBadgeSchema),
  /** No thumbnail-generation pipeline exists yet (`Resource`/`Asset` carry
   * no thumbnail/poster URL field beyond a VIDEO payload's optional
   * `posterAssetId`, which this v1 projection does not yet resolve to a
   * fetchable URL) — always `null` for now; the client renders its own
   * gradient placeholder (mirrors `apps/web/src/data/home.ts`'s
   * `CatalogueCard.thumbnail: string | null` "null -> placeholder"
   * contract). Flagged in the PR/handoff as a known v1 gap, not silently
   * dropped from the frozen shape. */
  thumbnailUrl: z.string().nullable(),
  isFreePreview: z.boolean(),
  /** Always `false` for `/v1/me/catalogue` today — every card returned there
   * has already passed the §4 access-set intersection (fail-closed: nothing
   * non-entitled is ever included), so nothing in THIS surface is a "locked
   * teaser" yet. The field ships now (frozen shape) so a future
   * cross-level upsell/teaser feature (surfacing a DIFFERENT level's
   * free-preview content as a marketing card) doesn't need a contract
   * change — see `services/catalogue.ts`'s header for the full rationale
   * and the fail-closed AC this preserves. */
  locked: z.boolean(),
});
export type CatalogueCard = z.infer<typeof catalogueCardSchema>;

/** `Page<CatalogueCard>` (ticket AC: `{items, nextCursor, totalEstimate?}`,
 * cursor pagination, no offset). */
export const meCatalogueResponseSchema = paginatedResponseSchema(catalogueCardSchema).extend({
  /** Cheap, EVENTUALLY-consistent estimate of the total entitled+filtered
   * set size — never load-bearing for pagination itself (only `nextCursor`
   * is), so a slightly stale count under concurrent writes is acceptable.
   * Omitted (not `null`) for a `q`-delegated response, since the upstream
   * FTS5 search endpoint's own contract has no total-count field to
   * project. */
  totalEstimate: z.number().int().nonnegative().optional(),
});
export type MeCatalogueResponse = z.infer<typeof meCatalogueResponseSchema>;

// ─── GET /v1/me/catalogue/summary ───────────────────────────────────────────

/** One Home stat tile ("87 · Simulations"). */
export const catalogueSummaryTileSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number().int().nonnegative(),
});
export type CatalogueSummaryTile = z.infer<typeof catalogueSummaryTileSchema>;

/** The Home stat tiles (ticket AC) — a read-only projection over the SAME §4
 * access set `/v1/me/catalogue` uses, never a second resolution path. Ships
 * structured counts (`tiles` is the ready-to-render board-tile list; the
 * named fields back it) rather than the richer marketing copy
 * `apps/web/src/data/home.ts`'s interim mock `CatalogueSummary` carries
 * (headline text, a featured-sim preview) — that copy is a
 * presentation-layer concern the frontend composes client-side, not
 * something this read-only backend surface should own; see this ticket's
 * handoff notes for the reconciliation flag. */
export const catalogueSummarySchema = z.object({
  totalResources: z.number().int().nonnegative(),
  simulationCount: z.number().int().nonnegative(),
  freePreviewCount: z.number().int().nonnegative(),
  /** Resources published within the same recency window `NEW` badges use
   * (`services/catalogue.ts`'s `NEW_BADGE_WINDOW_DAYS`). */
  newCount: z.number().int().nonnegative(),
  subjectCount: z.number().int().nonnegative(),
  levelCount: z.number().int().nonnegative(),
  tiles: z.array(catalogueSummaryTileSchema),
});
export type CatalogueSummary = z.infer<typeof catalogueSummarySchema>;

// ─── GET /v1/catalogue/public ───────────────────────────────────────────────
// Phase-5 note (ticket): route + DTO exist now, kept dark/gated (off by
// default) until phase 5 — `services/catalogue.ts`'s `isPublicCatalogueEnabled`.

export const publicCatalogueQuerySchema = paginationQuerySchema;
export type PublicCatalogueQuery = z.infer<typeof publicCatalogueQuerySchema>;

/** Same card shape as the authenticated surface (`catalogueCardSchema`) —
 * `isFreePreview` is always `true` and `locked` always `false` for every
 * card this route returns (ticket AC: "ONLY isFreePreview resources ...
 * no entitlement leak"), enforced server-side in the service, not just by
 * convention here. */
export const publicCatalogueResponseSchema = paginatedResponseSchema(catalogueCardSchema);
export type PublicCatalogueResponse = z.infer<typeof publicCatalogueResponseSchema>;
